import { Button } from '@ordo/ui/button'
import { Card } from '@ordo/ui/card'
import { Field, FieldLabel } from '@ordo/ui/field'
import { Input } from '@ordo/ui/input'
import { useState } from 'react'

/**
 * Plan 50 — `/forgot-password`. User enters email; we POST to
 * `/api/auth/password-reset`. Server always replies `{ ok: true }`
 * (account-enumeration protection), so the UI flips to a "Check your
 * email" confirmation state regardless of whether the email is on
 * file. The actual reset link lands in the user's inbox (or in
 * `/tmp/ordo-magic-link.log` in dev when COMMS_KEY is unset).
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (submitting) {
      return
    }
    setSubmitting(true)
    setError(null)
    const res = await fetch('/api/auth/password-reset', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email: email.trim() }),
    })
    setSubmitting(false)
    if (res.ok) {
      setSubmitted(true)
    } else {
      setError('Could not send reset link. Try again.')
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="flex w-full max-w-sm flex-col gap-6 p-8 shadow-md">
        <header className="text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Reset password</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {submitted ? "We'll send a link if your email is on file" : 'Enter your email and we’ll send a reset link.'}
          </p>
        </header>

        {error ? (
          <div className="rounded-md border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        ) : null}

        {!submitted ? (
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <Field>
              <FieldLabel htmlFor="forgot-email">Email</FieldLabel>
              <Input
                id="forgot-email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
              />
            </Field>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Sending…' : 'Send reset link'}
            </Button>
            <a href="/signin" className="text-center text-xs text-muted-foreground hover:underline">
              Back to sign in
            </a>
          </form>
        ) : (
          <div className="rounded-md border border-border bg-muted/40 p-4 text-center text-sm">
            <p className="font-medium">Check your email</p>
            <p className="mt-1 text-muted-foreground">
              If <strong>{email}</strong> is on file, we just sent a link to reset your password.
            </p>
            <a href="/signin" className="mt-3 inline-block text-xs text-muted-foreground hover:underline">
              Back to sign in
            </a>
          </div>
        )}
      </Card>
    </div>
  )
}
